import TileLayer from "ol/layer/Tile";
import { Layer, VectorLayerDMTR } from "../menu/view-menu/layer";
import { AirSpaceBoundaryModel } from "./air-space-boundary";
import { CoordinatePointModel } from "./coordinate-point";
import { NavaidModel } from "./navaid";
import { ProcedureModel } from "./procedure";
import TileSource from "ol/source/Tile";
import { cloneDeep, isEqual } from 'lodash';

export class ConfigurationModel {
  id:number;
  name:string;
  active: boolean;
  projectId: number;
  colorSettingsJson: string;
  cumulativeColorSettingsJson: string;

  // terrains: ConfigurationTerrain[] = [];
  calculation: CalculationSettingsModel = new CalculationSettingsModel();
  rnav: RNAVModel = new RNAVModel();
  configurationProcedure: ConfigurationProcedureModel = new ConfigurationProcedureModel();
  defaultAltitudes: DefaultAltitudesModel = new DefaultAltitudesModel();
  rcs: ConfigurationRCSModel[] = [];
  layers: ConfigurationLayerModel[] = [];
  display: ConfigurationDisplayModel = new ConfigurationDisplayModel();
  navaids: NavaidModel[] = [];
  coordPoints: CoordinatePointModel[] = [];
  procedures: ProcedureModel[] = [];
  airspaceBoundaries : AirSpaceBoundaryModel[] = [];
  isselected : boolean = false;

  facilityConfiguration: FacilityModel = new FacilityModel();
  directories: DirectoriesModel = new DirectoriesModel();

  network: NetworkModel = new NetworkModel();


  constructor(data? :ConfigurationModel){
    this.id = data?.id ?? null;
    this.name = data?.name ?? "";
    this.active = data?.active ?? false;
    this.projectId = data?.projectId ?? null;
  }

  allLayersVisible(){
    for (const layer of this.layers){
      if (!layer.visible && layer.enabled){
        return false
      }
    }
    return true;
  }

  static feetToNm(value : number){
    return value * 0.00016458;
  }

  static nmToFeet(value : number){
    return value * 6076.11549;
  }

  static feetToMeters(feet:any){
    return feet * 0.3048;
  }

  static metersToFeet(meters:any){
    return meters * 3.28084;
  }

  static nmToMeters(nm: any){
    return nm * 1852;
  }

  static metersToNm(meters: any){
    return meters * 0.0005399568;
  }

  static map(config: any): ConfigurationModel{
    var model = new ConfigurationModel();
    model.active = config.active;
    model.id = config.id;
    model.name = config.name;
    model.colorSettingsJson = config.colorSettingsJson;
    model.cumulativeColorSettingsJson = config.cumulativeColorSettingsJson;
    model.defaultAltitudes = DefaultAltitudesModel.map(config?.defaultAltitudes);
    model.calculation = CalculationSettingsModel.map(config?.calculationSettings);
    model.rnav = RNAVModel.map(config?.rnav);
    model.configurationProcedure = ConfigurationProcedureModel.map(config?.configurationProcedure);
    // config?.terrains.forEach(element => {
    //   model.terrains.push(ConfigurationTerrain.map(element));
    // });
    config?.rcss?.forEach(element => {
      model.rcs.push(ConfigurationRCSModel.map(element));
    });
    config?.layers.forEach(element => {
      model.layers.push(ConfigurationLayerModel.map(element));
    });
    model.display = ConfigurationDisplayModel.map(config.display);
    model.navaids = config.navaids;
    model.coordPoints = config.coordPoints;
    model.procedures = config.procedures;
    model.airspaceBoundaries = config.airspaceBoundaries;
    model.projectId = config.projectId;
    model.facilityConfiguration = FacilityModel.map(config?.facilityConfiguration);
    model.directories = DirectoriesModel.map(config?.directories);
    model.network = NetworkModel.map(config?.network);
    return model;
  };


  static mapToService(model: ConfigurationModel): any{
    var config : any = new Object;
    config.id = model.id;
    config.active = model.active;
    config.name = model.name;
    config.projectId = model.projectId;
    // config.terrains = [];
    // model.terrains.forEach(element => {
    //   config.terrains.push(ConfigurationTerrain.mapToService(element));
    // });
    config.calculationSettings = CalculationSettingsModel.mapToService(model.calculation);
    config.rnav = RNAVModel.mapToService(model.rnav);
    config.configurationProcedure = ConfigurationProcedureModel.mapToService(model.configurationProcedure);
    config.defaultAltitudes = DefaultAltitudesModel.mapToService(model.defaultAltitudes);
    config.rcss = [];
    model.rcs.forEach(element => {
      config.rcss.push(ConfigurationRCSModel.mapToService(element));
    });
    config.layers = [];
    model.layers.forEach(element => {
      config.layers.push(ConfigurationLayerModel.mapToService(element));
    });
    config.display = ConfigurationDisplayModel.mapToService(model.display);
    config.navaids = model.navaids;
    config.coordPoints = model.coordPoints;
    config.procedures = model.procedures;
    config.airspaceBoundaries = model.airspaceBoundaries;
    config.facilityConfiguration = FacilityModel.mapToService(model.facilityConfiguration);
    config.directories = model.directories;

    config.network = model.network;
    config.colorSettingsJson = model.colorSettingsJson;
    config.cumulativeColorSettingsJson = model.cumulativeColorSettingsJson;

    return config;
  }

  isEqual(other: ConfigurationModel): boolean {
    return isEqual(ConfigurationModel.mapToService(this), ConfigurationModel.mapToService(other));
  }


  clone(cloneLayers: boolean = false): ConfigurationModel {
    const cloned = new ConfigurationModel();

    cloned.id = this.id;
    cloned.name = this.name;
    cloned.active = this.active;
    cloned.projectId = this.projectId;
    cloned.colorSettingsJson = this.colorSettingsJson;
    cloned.cumulativeColorSettingsJson = this.cumulativeColorSettingsJson;
    cloned.isselected = this.isselected;

    cloned.calculation = cloneDeep(this.calculation);
    cloned.rnav = cloneDeep(this.rnav);
    cloned.configurationProcedure = cloneDeep(this.configurationProcedure);
    cloned.defaultAltitudes = cloneDeep(this.defaultAltitudes);
    cloned.rcs = cloneDeep(this.rcs);
    cloned.display = cloneDeep(this.display);
    cloned.navaids = cloneDeep(this.navaids);
    cloned.coordPoints = cloneDeep(this.coordPoints);
    cloned.procedures = cloneDeep(this.procedures);
    cloned.airspaceBoundaries = cloneDeep(this.airspaceBoundaries);
    cloned.facilityConfiguration = cloneDeep(this.facilityConfiguration);
    cloned.directories = cloneDeep(this.directories);
    cloned.network = cloneDeep(this.network);

    cloned.layers = [];
    for (const layer of this.layers) {
      const newLayer = new ConfigurationLayerModel();
      newLayer.id = layer.id;
      newLayer.configurationId = layer.configurationId;
      newLayer.name = layer.name;
      newLayer.path = layer.path;
      newLayer.active = layer.active;
      newLayer.type = layer.type;
      newLayer.enabled = layer.enabled;
      newLayer.visible = layer.visible;
      newLayer.isselected = layer.isselected;
      newLayer.isactive = layer.isactive;
      newLayer.defaultLayer = layer.defaultLayer;
      newLayer.defaultFillColor = layer.defaultFillColor;
      newLayer.defaultLineColor = layer.defaultLineColor;
      newLayer.defaultLabelProperty = layer.defaultLabelProperty;
      if (!cloneLayers) {
        // pass by reference -- not cloned
        newLayer.tileLayer = layer.tileLayer;
        newLayer.vectorLayer = layer.vectorLayer;
      } else {
        newLayer.tileLayer = cloneDeep(layer.tileLayer);
        newLayer.vectorLayer = cloneDeep(layer.vectorLayer);
      }

      cloned.layers.push(newLayer);
    }

    return cloned;
  }
}
export class DirectoriesModel{

  workingPath: string;
  localCachePath_3D: string;
  navAidDatabase: string;

  static map(data: any): DirectoriesModel{
    var model: DirectoriesModel = new DirectoriesModel();
    model.workingPath = data?.workingPath;
    model.localCachePath_3D = data?.localCachePath_3D;
    model.navAidDatabase = data?.navAidDatabase;

    return model
  }

  static mapToService(model: DirectoriesModel){
    let data: any  = new Object();
    data.workingPath = model.workingPath;
    data.localCachePath_3D = model.localCachePath_3D;
    data.navAidDatabase = model.navAidDatabase;
  }

}
export class NetworkModel{
  useProxyServer : boolean;
  address: String;
  port: number;
  exceptions: String;

  requiresAuthentication: boolean;
  user: String;
  password: String;


  static mapToService(model: NetworkModel){
    let data: any  = new Object();
    data.useProxyServer  = model.useProxyServer
    data.address = model.address
    data.port = model.port;
    data.exceptions = model.exceptions;

    data.requiresAuthentication = model.requiresAuthentication;
    data.user = model.user;
    data.password = model.password;

  }

  static map(data: any): NetworkModel{
    var model: NetworkModel = new NetworkModel();
    model.useProxyServer = data?.useProxyServer;
    model.address = data?.address;
    model.port = data?.port;

    model.exceptions = data?.exceptions;
    model.requiresAuthentication = data?.requiresAuthentication;
    model.password = data?.password;
    return model
  }




}
export class FacilityModel{
  configurationId: number;
  docMaxRange: number;

  docMaxAltitudeFT: number;
  docMaxAltitudeM: number;

  antenaMaxHeightFT: number;
  antenaMaxHeightM: number;

  aboveTerrainLimitFT: number;
  aboveTerrainLimitM: number;

  static map(data: any): FacilityModel
  {
    var model = new FacilityModel();
    model.configurationId = data?.configurationId;

    model.docMaxRange = data?.docMaxRange;

    model.docMaxAltitudeM = data?.docMaxAltitude;
    model.docMaxAltitudeFT = +ConfigurationModel.metersToFeet(model.docMaxAltitudeM).toFixed(2);

    model.antenaMaxHeightM = data?.antenaMaxHeight;
    model.antenaMaxHeightFT = +ConfigurationModel.metersToFeet(model.antenaMaxHeightM).toFixed(2);

    model.aboveTerrainLimitM = data?.aboveTerrainlimit;
    model.aboveTerrainLimitFT = +ConfigurationModel.metersToFeet(model.aboveTerrainLimitM).toFixed(2);

    return model;
  }
  static mapToService(model: FacilityModel){
    var facility : any = new Object();

    facility.docMaxAltitude = model.docMaxAltitudeM;

    facility.antenaMaxHeight = model.antenaMaxHeightM;

    facility.aboveTerrainlimit = model.aboveTerrainLimitM;

    facility.docMaxRange = model.docMaxRange;
    return facility;
  }
}

export class ProcedureSettingsModel {
  procAlongTrack : number;
  procWidth : number;
  prnavTma : number;
  prnavEnRoute : number;
  brnavEnRoute : number;
  arnpTma : number;
	arnpEnRoute : number;
  airportBoundary : number;
  altitudeBoundary : number;
}

export class CalculationSettingsModel {
  configurationId : number;
  kfactor: number;
  azimuthIncrement: number;
  sectorLength: number;
  summitGranularity: number;
  visibilityGranularity: number;
  visibilityScaling: string;
  autoFillTerrainVoids: boolean;
  coneOfSilenceDefa: number;
  useConeOfSilence: boolean;
  elevationType: string;
  useSectorization: boolean;
  rangeLimitation: string;
  algorithm: string;
  calculateNegAngles: boolean;
  distanceToNavAid: number;
  geometricConstraints : boolean;
  minSystemAltitude: number;
  maxSystemAltitude: number;

  static AZIMUTH_OPTIONS: string[] = ["0.3", "0.5", "1.0"];
  static PROPAGATION_OPTIONS: string[] = ["Line of sight", "Diffraction", "Diffraction (60% simplification)"/*, "Power Coverage"*/];
	static COVERAGE_ALGORITHM_OPTIONS: string[] = ["Screening", "Visibility"];
	static SECTOR_LENGTH_OPTIONS: string[] = [/*"100",*/ "500", "1000"]; // meters
	//static SUMMIT_GRANULARITY_OPTIONS: string[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]; Not needed. The calculation dll receives the value not an index.
	static VISIBILITY_GRANULARITY_OPTIONS: string[] = ["30", "60", "120"];
	static VISIBILITY_SCALING_OPTIONS: string[] = ["3x2", "1x1", "2x2", "3x3", "4x4"];
	static FACILITY_ELEVATION_OPTIONS: string[] = ["Published", "Relative"];
	static SECTOR_CELL_HEIGHT_OPTIONS: string[] = ["Consider Minimum Terrain Height", "Consider Maximum Terrain Height"];

  static map(data: any): CalculationSettingsModel{
    var model = new CalculationSettingsModel();
    model.configurationId = data?.configurationId;
    model.algorithm = data?.algorithm;
    model.autoFillTerrainVoids = data?.autoFillTerrainVoids;
    model.azimuthIncrement = data?.azimuthIncrement;
    model.calculateNegAngles = data?.calculateNegativeScreeningAngles;
    model.coneOfSilenceDefa = data?.coneOfSilenceDefa;
    model.distanceToNavAid = data?.distanceToNavaid;
    model.elevationType = data?.elevationType;
    model.kfactor = data?.kfactor;
    model.rangeLimitation = data?.rangeLimitation;
    model.sectorLength = data?.sectorLength;
    model.summitGranularity = data?.summitGranularity;
    model.useConeOfSilence = data?.useConeOfSilence;
    model.useSectorization = data?.useSectorization;
    model.visibilityGranularity = data?.visibilityGranularity;
    model.visibilityScaling = data?.visibilityScaling;
    return model;
  }

  static mapToService(model: CalculationSettingsModel): any {
    var calculation : any = new Object;
    calculation.algorithm = model.algorithm;
    calculation.autoFillTerrainVoids = model.autoFillTerrainVoids;
    calculation.azimuthIncrement = model.azimuthIncrement;
    calculation.calculateNegativeScreeningAngles = model.calculateNegAngles;
    calculation.coneOfSilenceDefa = model.coneOfSilenceDefa;
    calculation.distanceToNavaid = model.distanceToNavAid;
    calculation.elevationType = model.elevationType;
    calculation.kfactor = model.kfactor;
    calculation.rangeLimitation = model.rangeLimitation;
    calculation.sectorLength = model.sectorLength;
    calculation.summitGranularity = model.summitGranularity;
    calculation.useConeOfSilence = model.useConeOfSilence;
    calculation.useSectorization = model.useSectorization;
    calculation.visibilityGranularity = model.visibilityGranularity;
    calculation.visibilityScaling = model.visibilityScaling;
    return calculation;
  }

  static mapToCalculation(configuration: ConfigurationModel, algorithm? : string, range_limitation? : string): any{
    const model: CalculationSettingsModel = configuration.calculation;
    var calculation : any = new Object;
    calculation.configurationId = model.configurationId;
    calculation.algorithm = algorithm? this.COVERAGE_ALGORITHM_OPTIONS.indexOf(algorithm) : this.COVERAGE_ALGORITHM_OPTIONS.indexOf(model.algorithm);
    calculation.autoFillTerrainVoids = model.autoFillTerrainVoids;
    calculation.azimuthIncrement = this.AZIMUTH_OPTIONS.indexOf(model.azimuthIncrement.toString());
    calculation.calculateNegativeScreeningAngles = model.calculateNegAngles;
    calculation.coneOfSilenceDefa = model.coneOfSilenceDefa;
    calculation.distanceToNavaid = model.distanceToNavAid;
    calculation.elevationType = this.FACILITY_ELEVATION_OPTIONS.indexOf(model.elevationType);
    calculation.kfactor = model.kfactor;
    calculation.rangeLimitation = range_limitation? range_limitation : model.rangeLimitation;
    calculation.sectorLength = this.SECTOR_LENGTH_OPTIONS.indexOf(model.sectorLength.toString());
    calculation.summitGranularity = model.summitGranularity;
    calculation.useConeOfSilence = model.useConeOfSilence;
    calculation.useSectorization = model.useSectorization;
    calculation.visibilityGranularity = this.VISIBILITY_GRANULARITY_OPTIONS.indexOf(model.visibilityGranularity.toString());
    calculation.visibilityScaling = this.VISIBILITY_SCALING_OPTIONS.indexOf(model.visibilityScaling);
    calculation.geometricConstraints = false;
    calculation.minSystemAltitude = configuration.defaultAltitudes.minAltitude;
    calculation.maxSystemAltitude = configuration.defaultAltitudes.maxAltitude;
    return calculation;
  }
}

export class RNAVModel {
  configurationId : number;
  minDME: number;
  maxDME: number;
  maxElevationAngle: number;
  subtendedAngleRange: string;
  conventionalMaxRange: number;
  dopplerMaxRange: number;
  signalInSpaceErrorBudget: number;
  redundancy: boolean;
  nse: boolean;
  subtendedAngle: boolean;
  availablePairs: boolean;

  static SUBTENDED_ANGLE_OPTIONS: string[] = ["40-140", "30-150"];

  static map(data: any): RNAVModel{
    var model = new RNAVModel();
    model.configurationId = data?.configurationId;
    model.minDME = data?.minDmeFacilityRange;
    model.maxDME = data?.maxDmeFacilityRange;
    model.maxElevationAngle = data?.maxElevationAngle;
    model.subtendedAngleRange = data?.subtendedAngleRange;
    model.conventionalMaxRange = data?.conventionalMaxRange;
    model.dopplerMaxRange = data?.dopplerMaxRange;
    model.signalInSpaceErrorBudget = data?.signalInSpaceErrorBudget;
    model.redundancy = data?.redundancy;
    model.nse = data?.nse;
    model.subtendedAngle = data?.subtendedAngle;
    model.availablePairs = data?.availablePairs;
    return model;
  }

  static mapToService(model: RNAVModel): any {
    var rnav : any = new Object;
    rnav.minDmeFacilityRange = model.minDME;
    rnav.maxDmeFacilityRange = model.maxDME;
    rnav.maxElevationAngle = model.maxElevationAngle;
    rnav.subtendedAngleRange = model.subtendedAngleRange;
    rnav.conventionalMaxRange = model.conventionalMaxRange;
    rnav.dopplerMaxRange = model.dopplerMaxRange;
    rnav.signalInSpaceErrorBudget = model.signalInSpaceErrorBudget;
    rnav.redundancy = model.redundancy;
    rnav.nse = model.nse;
    rnav.subtendedAngle = model.subtendedAngle;
    rnav.availablePairs = model.availablePairs;
    return rnav;
  }

  static mapToCalculation(model: RNAVModel): any {
    var rnav : any = new Object;
    rnav.configurationId = model.configurationId;
    rnav.minDmeFacilityRange = model.minDME;
    rnav.maxDmeFacilityRange = model.maxDME;
    rnav.maxElevationAngle = model.maxElevationAngle;
    rnav.subtendedAngleRange = this.SUBTENDED_ANGLE_OPTIONS.indexOf(model.subtendedAngleRange.toString());
    rnav.conventionalMaxRange = model.conventionalMaxRange;
    rnav.dopplerMaxRange = model.dopplerMaxRange;
    rnav.signalInSpaceErrorBudget = model.signalInSpaceErrorBudget;
    rnav.redundancy = model.redundancy;
    rnav.nse = model.nse;
    rnav.subtendedAngle = model.subtendedAngle;
    rnav.availablePairs = model.availablePairs;
    return rnav;
  }
}

export class ConfigurationProcedureModel {
  configurationId : number;
  bRnavEnRoute: number;
  pRnavTma: number;
  pRnavEnRoute: number;
  aRnpTma: number;
  aRnpEnRoute: number;
  distanceFromArp: number;
  distanceFromArpM: number;
  distanceFromArpNM: number;
  altitude: number;
  altitudeM: number;
  altitudeFt: number;
  procedureAlongtrack: number;
  procedureAlongtrackNM: number;
  procedureAlongtrackM: number;
  procedureWidth: number;
  procedureWidthNM: number;
  procedureWidthM: number;

  static mapToCalculation(model: ConfigurationProcedureModel): ProcedureSettingsModel {
    var proc : ProcedureSettingsModel = new ProcedureSettingsModel;
    proc.procAlongTrack = model.procedureAlongtrack;
    proc.procWidth = model.procedureWidth;
    proc.prnavTma = model.pRnavTma;
    proc.prnavEnRoute = model.pRnavEnRoute;
    proc.brnavEnRoute = model.bRnavEnRoute;
    proc.arnpTma = model.aRnpTma;
    proc.arnpEnRoute = model.aRnpEnRoute;
    proc.altitudeBoundary = model.altitudeM;
    proc.airportBoundary = model.distanceFromArp;
    return proc;
}

  static map(data: any): ConfigurationProcedureModel{
    var model = new ConfigurationProcedureModel();
    model.configurationId = data?.configurationId;
    model.bRnavEnRoute = data?.bRnavEnroute;
    model.pRnavTma = data?.pRnavTma;
    model.pRnavEnRoute = data?.pRnavEnroute;
    model.aRnpTma = data?.aRnpTma;
    model.aRnpEnRoute = data?.aRnpEnroute;
    model.distanceFromArp = data?.distanceFromArp;
    model.distanceFromArpM = ConfigurationModel.nmToMeters(data?.distanceFromArp);
    model.distanceFromArpNM = data?.distanceFromArp;
    model.altitude = data?.altitude;
    model.altitudeM = ConfigurationModel.feetToMeters(data?.altitude);
    model.altitudeFt = data?.altitude;
    model.procedureAlongtrack = data?.procedureAlongtrack;
    model.procedureAlongtrackM = ConfigurationModel.nmToMeters(data?.procedureAlongtrack);
    model.procedureAlongtrackNM = data?.procedureAlongtrack;
    model.procedureWidth = data?.procedureWidth;
    model.procedureWidthM = ConfigurationModel.nmToMeters(data?.procedureWidth);
    model.procedureWidthNM = data?.procedureWidth;
    return model;
  }

  static mapToService(model: ConfigurationProcedureModel): any {
    var procedure: any = new Object;
    procedure.bRnavEnroute = model.bRnavEnRoute;
    procedure.pRnavTma = model.pRnavTma;
    procedure.pRnavEnroute = model.pRnavEnRoute;
    procedure.aRnpTma = model.aRnpTma;
    procedure.aRnpEnroute = model.aRnpEnRoute;
    procedure.distanceFromArp = model.distanceFromArp;
    procedure.altitude = model.altitude;
    procedure.procedureAlongtrack = model.procedureAlongtrack;
    procedure.procedureWidth = model.procedureWidth;
    return procedure;
  }
}

export class DefaultAltitudesModel{
  configurationId: number;
  singleLevelAltitude: number;
  singleLevelAltitudeM: number;
  singleLevelAltitudeFT: number;
  maxAltitude: number;
  maxAltitudeM: number;
  maxAltitudeFT: number;
  altitudeIncrement: number;
  altitudeIncrementM: number;
  altitudeIncrementFT: number;
  minAltitude: number;
  minAltitudeM: number;
  minAltitudeFT: number;

  static map(data: any): DefaultAltitudesModel{
    var model = new DefaultAltitudesModel();
    model.configurationId = data?.configurationId;
    model.altitudeIncrement = data?.altitudeIncrement;
    model.altitudeIncrementFT = data?.altitudeIncrement;
    model.altitudeIncrementM = ConfigurationModel.feetToMeters(data?.altitudeIncrement);
    model.singleLevelAltitude = data?.singleLevelAltitude;
    model.singleLevelAltitudeFT = data?.singleLevelAltitude;
    model.singleLevelAltitudeM = ConfigurationModel.feetToMeters(data?.singleLevelAltitude);
    model.maxAltitude = data?.maxAltitude;
    model.maxAltitudeFT = data?.maxAltitude;
    model.maxAltitudeM = ConfigurationModel.feetToMeters(data?.maxAltitude);
    model.minAltitude = data?.minAltitude;
    model.minAltitudeFT = data?.minAltitude;
    model.minAltitudeM = ConfigurationModel.feetToMeters(data?.minAltitude);
    return model;
  }

  static mapToService(data: DefaultAltitudesModel): any {
    var model : any = new Object;
    model.altitudeIncrement = data.altitudeIncrement;
    model.maxAltitude = data.maxAltitude;
    model.minAltitude = data.minAltitude;
    model.singleLevelAltitude = data.singleLevelAltitude;
    return model;
  }
}

export class ConfigurationRCSModel{
  id: number;
  configurationId: number;
  epsg: string;
  name: string;
  active: boolean = false;
  enabled: boolean;
  isselected : boolean = false;
  isactive: boolean = false;

  static map(data: any): ConfigurationRCSModel{
    var model = new ConfigurationRCSModel();
    model.enabled = data?.active;
    model.configurationId = data?.configurationId;
    model.epsg = data?.epsg;
    model.id = data?.id;
    model.name = data?.name;
    return model;
  }

  static mapToService(rcs: ConfigurationRCSModel): any {
    var model: any = new Object;
    model.id = rcs.id;
    model.active = rcs.enabled;
    model.configurationId = rcs.configurationId;
    model.epsg = rcs.epsg;
    model.name = rcs.name;
    return model;
  }
}
export class ConfigurationLayerModel{
  id: number;
  configurationId: number;
  name: string;
  path: string;
  active: boolean = false;
  type: string;
  enabled: boolean = false;
  visible: boolean = false;
  isselected : boolean = false;
  isactive: boolean = false;
  tileLayer: TileLayer<TileSource>;
  vectorLayer: Layer;
  defaultLayer: boolean = false;
  defaultFillColor: string;
  defaultLineColor: string;
  defaultLabelProperty: string;

  static map(data: any): ConfigurationLayerModel{
    var model = new ConfigurationLayerModel();
    model.enabled = data?.active;
    model.configurationId = data?.configurationId;
    model.path = data?.path;
    model.type = data?.type;
    model.id = data?.id;
    model.name = data?.name;
    model.defaultLayer = data?.defaultLayer;
    model.defaultFillColor = data?.defaultFillColor;
    model.defaultLineColor = data?.defaultLineColor;
    model.defaultLabelProperty = data?.defaultLabelProperty;
    return model;
  }

  static mapToService(layer: ConfigurationLayerModel): any {
    var model: any = new Object;
    model.id = layer.id;
    model.active = layer.enabled;
    model.configurationId = layer.configurationId;
    model.path = layer.path;
    model.type = layer.type;
    model.name = layer.name;
    model.visible = layer.visible;
    model.defaultLayer = layer.defaultLayer;
    model.defaultFillColor = layer.defaultFillColor;
    model.defaultLineColor = layer.defaultLineColor;
    model.defaultLabelProperty = layer.defaultLabelProperty;
    return model;
  }
}


export class ConfigurationDisplayModel{
  configurationId: number;
  mapBackground: string;
  coordinateFormat: string;
  horizontalUnits: string;
  startDefaultArea: boolean;

  static map(data: any): ConfigurationDisplayModel{
    var model = new ConfigurationDisplayModel();
    model.configurationId = data?.configurationId;
    model.mapBackground = data?.mapBackground;
    model.coordinateFormat = data?.coordinateFormat;
    model.horizontalUnits = data?.horizontalUnits;
    model.startDefaultArea = data?.startDefaultArea;
    return model;
  }

  static mapToService(data: ConfigurationDisplayModel): any {
    var model : any = new Object;
    model.mapBackground = data.mapBackground;
    model.coordinateFormat = data.coordinateFormat;
    model.horizontalUnits = data.horizontalUnits;
    model.startDefaultArea = data.startDefaultArea;
    return model;
  }
}
